import React, { forwardRef, useEffect, useState } from 'react';

import { Close } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import Popover from '@mui/material/Popover';

import { useEventEmitter } from '@ezbobdev/ui-commons';
import { useTranslation } from '@ezbobdev/ui-framework';

import { css, styled } from '../../../Utils/style';

const PopoverStyled = styled(Popover)`
  ${({ theme }) => css`
    .content {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(3)};
      padding: ${theme.spacing(10)} ${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(3)};

      .helpPopoverButton {
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
        padding: 0;

        &:focus {
          outline: #000 2px solid;
          border-radius: 0.3rem;
        }

        img {
          width: auto;
          height: 64px;
        }
      }
    }

    & .MuiPaper-root {
      width: 100%;
    }

    .close-button {
      position: absolute;
      top: ${theme.spacing(3)};
      /* @noflip */
      left: ${theme.spacing(3)};
    }

    .workingHours {
      margin: 0 0 ${theme.spacing(1)} 0;
    }

    .phoneLink {
      a {
        text-decoration: none;
      }
    }

    ${theme.breakpoints.up('sm')} {
      & .MuiPaper-root {
        width: 35.8rem;
      }
    }
  `}
`;

export const CustomPopoverElement = forwardRef((props, ref) => {
  const { content, iconAlt, iconSrc, state, handleOpen, label } = props;
  const { t } = useTranslation();
  const { emitEvent } = useEventEmitter();
  const [anchorEl, setAnchorEl] = state;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handlePopoverButtonClick = (e) => {
    emitEvent(e.type, { label });
    handleOpen();
  };

  const handleCloseButtonClick = (e) => {
    emitEvent(e.type, { label: t(`globals.popupClose`) });
    handleClose();
  };

  const open = Boolean(anchorEl);

  const [closeRef, setCloseRef] = useState(null);

  useEffect(() => {
    if (closeRef) {
      // this is because the appear animation takes 200ms.
      // If we do this immediately, we'll see only partial background
      const ref = setTimeout(() => {
        closeRef?.focus();
      }, 200);
      return () => clearTimeout(ref);
    }
  }, [closeRef]);

  return (
    <div>
      <IconButton aria-haspopup="dialog" aria-label={label} ref={ref} onClick={handlePopoverButtonClick}>
        <img src={iconSrc} alt={iconAlt} />
      </IconButton>
      <PopoverStyled
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        role="dialog"
        aria-label={label}
      >
        <div className="content">
          <IconButton
            ref={setCloseRef}
            className="close-button"
            aria-label={t(`globals.popupClose`)}
            data-testid="close-icon-button"
            onClick={handleCloseButtonClick}
          >
            <Close />
          </IconButton>

          {content}
        </div>
      </PopoverStyled>
    </div>
  );
});
